import React, { useState, useEffect, useMemo } from 'react'
import { 
  FileText, 
  Calculator, 
  Building2, 
  FileCheck, 
  TrendingUp, 
  AlertCircle,
  CheckCircle,
  Clock,
  DollarSign,
  Calendar,
  AlertTriangle,
  Edit2,
  MapPin,
  LayoutDashboard,
  Layers,
  Shield,
  ArrowRight,
  Sparkles,
  Lock,
  CheckCircle2,
  X,
  ChevronLeft,
  ChevronRight
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { useExchange } from '../contexts/ExchangeContext'
import { Card } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Progress } from '../components/ui/ProgressBar'
import { LoadingSpinner } from '../components/ui/LoadingSpinner'
import { ExchangeWizardHub } from '../components/wizard/ExchangeWizardHub'
import { ExchangeHub } from '../components/wizard/ExchangeHub'
import { PurchaseHub } from '../components/wizard/PurchaseHub'
import { DisbursementHub } from '../components/wizard/DisbursementHub'
import { propertyApi } from '../services/api/property'
import toast from 'react-hot-toast'
import { useHubAccess } from '../hooks/useHubAccess'
import { KYCVerificationModal } from '../components/auth/KYCVerificationModal'
import { activityLog } from '../services/activityLog'
import { dataPersistence } from '../services/dataPersistence'
 

export const DashboardPage: React.FC = () => {
  const { user, isIdentityVerified } = useAuth()
  const [isAllActivityModalOpen, setIsAllActivityModalOpen] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 10
  const [isWizardOpen, setIsWizardOpen] = useState(false)
  const demoGuidanceToastId = 'demo1-guidance'
  const demoGuidanceStageRef = React.useRef<string>('')
  const demoGuidanceTimeoutRef = React.useRef<number | null>(null)

  const toggleHubLock = (hubType: 'sales' | 'identification' | 'purchase' | 'disbursement' | 'irs') => {
    const lockKey = `hub_locked_${hubType}`
    const isLocked = localStorage.getItem(lockKey) === 'true'
    if (isLocked) {
      localStorage.removeItem(lockKey)
    } else {
      localStorage.setItem(lockKey, 'true')
    }
    window.dispatchEvent(new CustomEvent('wizardDataUpdated'))
  }

  const isHubLocked = (hubType: 'sales' | 'identification' | 'purchase' | 'disbursement' | 'irs'): boolean => {
    return localStorage.getItem(`hub_locked_${hubType}`) === 'true'
  }
  const [isExchangeHubOpen, setIsExchangeHubOpen] = useState(false)
  const [isPurchaseHubOpen, setIsPurchaseHubOpen] = useState(false)
  const [isDisbursementHubOpen, setIsDisbursementHubOpen] = useState(false)
  const [wizardInitialTab, setWizardInitialTab] = useState<'initial' | 'exchange' | 'acquisition'>('initial')
  const hasCheckedNewUserRef = React.useRef(false)
  const [showKYCModal, setShowKYCModal] = useState(false)
  const [userActivities, setUserActivities] = useState(activityLog.getActivities())
  const [uploadedDocsCount, setUploadedDocsCount] = useState(0)
  const [calculatorSavings, setCalculatorSavings] = useState<string | null>(null)
  const [currentTime, setCurrentTime] = useState(new Date())
  const [propertySaleDate, setPropertySaleDate] = useState<Date | null>(null)
  const [relinquishedAddress, setRelinquishedAddress] = useState<string>('')
  const [relinquishedCity, setRelinquishedCity] = useState<string>('')
  const [relinquishedStateCode, setRelinquishedStateCode] = useState<string>('')
  const [relinquishedZip, setRelinquishedZip] = useState<string>('')
  const [relinquishedClosingDate, setRelinquishedClosingDate] = useState<string | null>(null)
  const [relinquishedImage, setRelinquishedImage] = useState<string>('')
  const [relinquishedPrice, setRelinquishedPrice] = useState<string | null>(null)
  const [relinquishedBeds, setRelinquishedBeds] = useState<number | null>(null)
  const [relinquishedBaths, setRelinquishedBaths] = useState<number | null>(null)
  const [relinquishedSqft, setRelinquishedSqft] = useState<number | null>(null)
  const [zillowUrl, setZillowUrl] = useState<string | null>(null)
  const [redfinUrl, setRedfinUrl] = useState<string | null>(null)
  const [hasStartedProcess, setHasStartedProcess] = useState(() => {
    const activities = activityLog.getActivities()
    return activities.some(a => a.type === 'document' && a.title.includes('Sales & Purchase Agreement'))
  })
  const [hubRefreshTrigger, setHubRefreshTrigger] = useState(0)

  const { currentExchange } = useExchange()

  // Helper to check if user is a demo user
  const isDemoUser = useMemo(() => {
    if (!user?.email) return false
    const userEmail = String(user.email).toLowerCase().trim()
    return userEmail === 'demo@open1031.ai' || userEmail === 'demo1@open1031.ai'
  }, [user?.email])
  const getSalesHubWizardData = React.useCallback(() => {
    const exchangeId = currentExchange?.id || user?.id || 'anon'
    const keys = [
      `sale_wizard_v1_${exchangeId}_phase1`,
      `sale_wizard_v1_${exchangeId}_initial`,
      `exchange_wizard_v1_${exchangeId}_initial`
    ]
    for (const key of keys) {
      const raw = localStorage.getItem(key)
      if (!raw) continue
      try {
        return JSON.parse(raw)
      } catch {}
    }
    return null
  }, [currentExchange?.id, user?.id])
  const computeDemoGuidanceStage = React.useCallback((): { stageKey: string; message: string } | null => {
    if (!user?.email || String(user.email).toLowerCase().trim() !== 'demo1@open1031.ai') return null
    if (!isIdentityVerified) {
      return { stageKey: 'kyc', message: 'Step 1: Complete KYC identity verification to unlock the Sales Hub.' }
    }
    const salesHubSubmitted = localStorage.getItem('sales_hub_submitted') === 'true'
    const identificationHubSubmitted = localStorage.getItem('identification_hub_submitted') === 'true'
    const purchaseHubSubmitted = localStorage.getItem('purchase_hub_submitted') === 'true'
    const disbursementHubSubmitted = localStorage.getItem('disbursement_hub_submitted') === 'true'
    const acquisitionHubSubmitted = localStorage.getItem('acquisition_hub_submitted') === 'true'
    if (!salesHubSubmitted) {
      const wizard = getSalesHubWizardData()
      const exchangeInfoComplete = !!wizard?.exchangeType
      if (!exchangeInfoComplete) {
        return { stageKey: 'sales.exchange-info', message: 'Step 2: Sales Hub — select your exchange type and answer the exchange questions.' }
      }
      const spaExtracted = wizard?.hasExtractedWithNeela === true
      const spaUploaded = wizard?.salesPurchaseAgreementUploaded === true
      const titleUploaded = wizard?.preliminaryTitleReportUploaded === true
      const titleExtracted = wizard?.hasTitleReportExtracted === true
      const escrowRequired = wizard?.isInEscrow === true
      if (!spaUploaded) {
        return { stageKey: 'sales.upload-spa', message: 'Step 2: Sales Hub — upload your Sales & Purchase Agreement.' }
      }
      if (escrowRequired && !titleUploaded) {
        return { stageKey: 'sales.upload-title', message: 'Step 2: Sales Hub — upload your Preliminary Title Report (required when in escrow).' }
      }
      if (!spaExtracted) {
        return { stageKey: 'sales.extract-spa', message: 'Step 2: Sales Hub — run Neela extraction on the Sales & Purchase Agreement.' }
      }
      if (titleUploaded && !titleExtracted) {
        return { stageKey: 'sales.extract-title', message: 'Step 2: Sales Hub — run Neela extraction on the Preliminary Title Report.' }
      }
      const exchangers = Array.isArray(wizard?.exchangers) ? wizard.exchangers : []
      const exchangersComplete = exchangers.length > 0 && exchangers.every((e: any) => {
        const hasAddress = !!(e.homeAddress || (e.street && e.city && e.state && e.zipCode))
        return !!(e.firstName && e.lastName && e.email && e.phone && hasAddress && e.taxIdNumber)
      })
      if (!exchangersComplete) {
        return { stageKey: 'sales.exchangers', message: 'Step 2: Sales Hub — confirm exchanger details.' }
      }
      const propertyDetailsComplete = !!(
        wizard?.relinquished?.addressOrLegalDescription &&
        wizard?.buyer?.names &&
        wizard?.closingCompanySale?.name &&
        wizard?.closingCompanySale?.address &&
        wizard?.closingCompanySale?.phone &&
        wizard?.closingCompanySale?.closerName &&
        wizard?.closingCompanySale?.closerEmail
      )
      if (!propertyDetailsComplete) {
        return { stageKey: 'sales.property-details', message: 'Step 2: Sales Hub — confirm property, buyer, and closing details.' }
      }
      const signaturesComplete = localStorage.getItem('exchange_agreement_signed') === 'true' && localStorage.getItem('w9_signed') === 'true'
      if (!signaturesComplete) {
        return { stageKey: 'sales.signatures', message: 'Step 2: Sales Hub — e-sign the Exchange Agreement and W-9.' }
      }
      return { stageKey: 'sales.submit', message: 'Step 2: Sales Hub — submit the Sales Hub section.' }
    }
    if (!identificationHubSubmitted) {
      return { stageKey: 'identification', message: 'Step 3: Identification Hub — complete and submit the Identification Form.' }
    }
    if (!purchaseHubSubmitted) {
      return { stageKey: 'purchase', message: 'Step 4: Purchase Hub — complete and submit the Purchase Hub section.' }
    }
    if (!disbursementHubSubmitted) {
      return { stageKey: 'disbursement', message: 'Step 5: Disbursement Hub — complete and submit the Disbursement Form.' }
    }
    if (!acquisitionHubSubmitted) {
      return { stageKey: 'acquisition', message: 'Step 6: Acquisition & IRS — complete and submit the final hub.' }
    }
    return { stageKey: 'done', message: 'All hubs completed. You are ready for final review.' }
  }, [getSalesHubWizardData, isIdentityVerified, user?.email])
  const updateDemoGuidanceAlert = React.useCallback(() => {
    const next = computeDemoGuidanceStage()
    if (!next) return
    const prevStageKey = demoGuidanceStageRef.current
    const isNewStage = prevStageKey !== next.stageKey
    if (!isNewStage) return
    if (demoGuidanceTimeoutRef.current) {
      window.clearTimeout(demoGuidanceTimeoutRef.current)
      demoGuidanceTimeoutRef.current = null
    }
    const showInfo = () => {
      toast(next.message, { id: demoGuidanceToastId, duration: 3500 })
      demoGuidanceStageRef.current = next.stageKey
    }
    const showSuccessThenContinue = (successMessage: string) => {
      toast.success(successMessage, { id: demoGuidanceToastId, duration: 2000 })
      demoGuidanceStageRef.current = next.stageKey
      demoGuidanceTimeoutRef.current = window.setTimeout(() => {
        demoGuidanceStageRef.current = ''
        updateDemoGuidanceAlert()
      }, 2100)
    }
    if (prevStageKey === 'kyc' && next.stageKey.startsWith('sales')) {
      showSuccessThenContinue('KYC completed.')
      return
    }
    if (prevStageKey.startsWith('sales') && next.stageKey === 'identification') {
      showSuccessThenContinue('Sales Hub completed.')
      return
    }
    if (prevStageKey === 'identification' && next.stageKey === 'purchase') {
      showSuccessThenContinue('Identification Hub completed.')
      return
    }
    if (prevStageKey === 'purchase' && next.stageKey === 'disbursement') {
      showSuccessThenContinue('Purchase Hub completed.')
      return
    }
    if (prevStageKey === 'disbursement' && next.stageKey === 'acquisition') {
      showSuccessThenContinue('Disbursement Hub completed.')
      return
    }
    if (prevStageKey === 'acquisition' && next.stageKey === 'done') {
      showSuccessThenContinue('Final hub completed.')
      return
    }
    showInfo()
  }, [computeDemoGuidanceStage])
  useEffect(() => {
    if (!user?.email || String(user.email).toLowerCase().trim() !== 'demo1@open1031.ai') return
    updateDemoGuidanceAlert()
    const handler = () => updateDemoGuidanceAlert()
    window.addEventListener('salesHubCompleted', handler)
    window.addEventListener('identificationHubCompleted', handler)
    window.addEventListener('purchaseHubCompleted', handler)
    window.addEventListener('disbursementHubCompleted', handler)
    window.addEventListener('acquisitionHubCompleted', handler)
    return () => {
      window.removeEventListener('salesHubCompleted', handler)
      window.removeEventListener('identificationHubCompleted', handler)
      window.removeEventListener('purchaseHubCompleted', handler)
      window.removeEventListener('disbursementHubCompleted', handler)
      window.removeEventListener('acquisitionHubCompleted', handler)
      if (demoGuidanceTimeoutRef.current) {
        window.clearTimeout(demoGuidanceTimeoutRef.current)
        demoGuidanceTimeoutRef.current = null
      }
      toast.dismiss(demoGuidanceToastId)
      demoGuidanceStageRef.current = ''
    }
  }, [updateDemoGuidanceAlert, user?.email])

  // Hub access control
  const salesAccess = useHubAccess('sales', user, isIdentityVerified)
  const purchaseAccess = useHubAccess('purchase', user, isIdentityVerified)
  const identificationAccess = useHubAccess('identification', user, isIdentityVerified)
  const disbursementAccess = useHubAccess('disbursement', user, isIdentityVerified)

  // Determine next actionable step
  const getNextStep = (): { hub: string; label: string; action: () => void } | null => {
    if (!isIdentityVerified) {
      return { hub: 'kyc', label: 'Verify Your Identity', action: () => setShowKYCModal(true) }
    }
    if (!salesAccess.isComplete) {
      return { hub: 'sales', label: 'Complete Sales Hub', action: () => { setWizardInitialTab('initial'); setIsWizardOpen(true) } }
    }
    if (!identificationAccess.isComplete && identificationAccess.canAccess) {
      return { hub: 'identification', label: 'Completed Identification Hub', action: () => setIsExchangeHubOpen(true) }
    }
    if (!purchaseAccess.isComplete && purchaseAccess.canAccess) {
      return { hub: 'purchase', label: 'Complete Purchase Hub', action: () => setIsPurchaseHubOpen(true) }
    }
    if (!disbursementAccess.isComplete && disbursementAccess.canAccess) {
      return { hub: 'disbursement', label: 'Complete Disbursement Hub', action: () => setIsDisbursementHubOpen(true) }
    }
    return null
  }

  const nextStep = getNextStep()

  // Update timer every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date())
    }, 60000) // Update every minute
    return () => clearInterval(timer)
  }, [])

  // Listen for activity updates
  useEffect(() => {
    const handleActivityAdded = () => {
      setUserActivities(activityLog.getActivities())
    }
    window.addEventListener('activityAdded', handleActivityAdded)
    return () => window.removeEventListener('activityAdded', handleActivityAdded)
  }, [])

  // Listen for sales hub completion
  useEffect(() => {
    const handleSalesHubCompleted = () => {
      setHubRefreshTrigger(prev => prev + 1)
      setUserActivities(activityLog.getActivities())
    }
    window.addEventListener('salesHubCompleted', handleSalesHubCompleted)
    return () => window.removeEventListener('salesHubCompleted', handleSalesHubCompleted)
  }, [])

  // Track uploaded documents and update hasStartedProcess when docs are uploaded AND Neela has extracted
  useEffect(() => {
    const checkDocuments = () => {
      // Check Sales Hub wizard data for uploaded documents
      const exchangeId = currentExchange?.id || user?.id || 'anon'
      const key1 = `exchange_wizard_v1_${exchangeId}_initial`
      const key2 = `sale_wizard_v1_${exchangeId}_initial`
      const key3 = `sale_wizard_v1_${exchangeId}_phase1`
      const raw1 = localStorage.getItem(key1)
      const raw2 = localStorage.getItem(key2)
      const raw3 = localStorage.getItem(key3)
      const raw = raw3 || raw2 || raw1
      
      let hasSalesPurchaseAgreement = false
      let hasPreliminaryTitle = false
      let hasExtractedWithNeela = false
      
      if (raw) {
        try {
          const parsed = JSON.parse(raw)
          hasSalesPurchaseAgreement = parsed.salesPurchaseAgreementUploaded === true
          hasPreliminaryTitle = parsed.preliminaryTitleReportUploaded === true
          // Only update dashboard metrics if Neela has extracted data
          hasExtractedWithNeela = parsed.hasExtractedWithNeela === true
          
          // Check if Sales Hub has meaningful data (user has filled out fields)
          const hasSalesHubData = !!(parsed?.relinquished?.addressOrLegalDescription?.trim() || 
                                    parsed?.exchangerPhaseOne?.names?.trim() ||
                                    parsed?.relinquished?.closingDate?.trim())
          
          // Don't update dashboard metrics unless:
          // 1. Neela has extracted data, AND
          // 2. Sales Hub has meaningful data (user has filled out fields)
          if (!hasExtractedWithNeela || !hasSalesHubData) {
            return
          }
        } catch {}
      } else {
        // No wizard data - don't update metrics
        return
      }
      
      // Fallback to activity log if wizard data not available
      if (!hasSalesPurchaseAgreement) {
        hasSalesPurchaseAgreement = userActivities.some(
          activity => activity.type === 'document' && activity.title.includes('Sales & Purchase Agreement')
        )
      }
      if (!hasPreliminaryTitle) {
        hasPreliminaryTitle = userActivities.some(
          activity => activity.type === 'document' && activity.title.includes('Preliminary Title Report')
        )
      }
      
      let count = 0
      if (hasSalesPurchaseAgreement) count++
      if (hasPreliminaryTitle) count++
      
      setUploadedDocsCount(count)
      
      // If user has uploaded the Sales & Purchase Agreement AND Neela has extracted, mark process as started
      if (hasSalesPurchaseAgreement && !hasStartedProcess) {
        setHasStartedProcess(true)
      }
      
      // Set sale date - use future date if property is in escrow and both documents uploaded, otherwise today
      // Only set if Neela has extracted data
      if (hasSalesPurchaseAgreement && !propertySaleDate) {
        // Check if property is in escrow from wizard data
        const exchangeId = currentExchange?.id || user?.id || 'anon'
        const key1 = `exchange_wizard_v1_${exchangeId}_initial`
        const key2 = `sale_wizard_v1_${exchangeId}_initial`
        const raw1 = localStorage.getItem(key1)
        const raw2 = localStorage.getItem(key2)
        const raw = raw2 || raw1
        
        let isInEscrow = false
        if (raw) {
          try {
            const parsed = JSON.parse(raw)
            isInEscrow = parsed.isInEscrow === true
          } catch {}
        }
        
        // If property is in escrow and both documents are uploaded, use future date (30 days from now)
        const saleDate = (isInEscrow && hasSalesPurchaseAgreement && hasPreliminaryTitle)
          ? new Date(Date.now() + 30 * 24 * 60 * 60 * 1000) // 30 days from now
          : new Date() // Today
        
        setPropertySaleDate(saleDate)
        setRelinquishedClosingDate(saleDate.toISOString().split('T')[0])
        localStorage.setItem('property_sale_date', saleDate.toISOString())
      }
    }
    
    checkDocuments()
    
    // Listen for wizard data updates and activity additions
    const handleWizardUpdate = () => {
      checkDocuments()
      loadRelinquishedPropertyData()
      // Force a re-render by updating currentTime to trigger recalculation
      setCurrentTime(new Date())
    }
    
    const handleActivityAdded = () => {
      checkDocuments()
    }
    
    const handleHubLockUpdate = () => {
      setHubRefreshTrigger(prev => prev + 1)
    }
    
    window.addEventListener('wizardDataUpdated', handleWizardUpdate)
    window.addEventListener('activityAdded', handleActivityAdded)
    window.addEventListener('wizardDataUpdated', handleHubLockUpdate)
    return () => {
      window.removeEventListener('wizardDataUpdated', handleWizardUpdate)
      window.removeEventListener('activityAdded', handleActivityAdded)
      window.removeEventListener('wizardDataUpdated', handleHubLockUpdate)
    }
  }, [userActivities, hasStartedProcess, propertySaleDate, currentExchange?.id, user?.id])

  useEffect(() => {
    if (isAllActivityModalOpen) {
      setCurrentPage(1)
    }
  }, [isAllActivityModalOpen])

  // Load calculator savings and property sale date from localStorage
  useEffect(() => {
    const savedCalculation = localStorage.getItem('calculator_savings')
    if (savedCalculation) {
      try {
        const parsed = JSON.parse(savedCalculation)
        setCalculatorSavings(parsed.savings)
      } catch {}
    }
    
    // Load property sale date from localStorage if previously set
    // Only load if it exists AND wizard data confirms it should be set
    const savedSaleDate = localStorage.getItem('property_sale_date')
    if (savedSaleDate) {
      try {
        const date = new Date(savedSaleDate)
        if (!isNaN(date.getTime())) {
          // Verify that wizard data exists and has meaningful data before setting sale date
          const exchangeId = currentExchange?.id || user?.id || 'anon'
          const key1 = `exchange_wizard_v1_${exchangeId}_initial`
          const key2 = `sale_wizard_v1_${exchangeId}_initial`
          const raw1 = localStorage.getItem(key1)
          const raw2 = localStorage.getItem(key2)
          const raw = raw2 || raw1
          
          if (raw) {
            try {
              const parsed = JSON.parse(raw)
              const addressOrDesc = parsed?.relinquished?.addressOrLegalDescription || parsed?.relinquished?.address
              const hasData = (addressOrDesc && addressOrDesc.trim() !== '') || 
                             (parsed?.exchangerPhaseOne?.names && parsed.exchangerPhaseOne.names.trim() !== '') ||
                             (parsed?.relinquished?.closingDate && parsed.relinquished.closingDate.trim() !== '')
              
              if (hasData) {
                setPropertySaleDate(date)
                setRelinquishedClosingDate(date.toISOString().split('T')[0])
              } else {
                // No meaningful data, clear the sale date
                setPropertySaleDate(null)
                setRelinquishedClosingDate(null)
                localStorage.removeItem('property_sale_date')
              }
            } catch {
              // If parsing fails, don't set the date
              setPropertySaleDate(null)
              setRelinquishedClosingDate(null)
            }
          } else {
            // No wizard data, clear the sale date
            setPropertySaleDate(null)
            setRelinquishedClosingDate(null)
            localStorage.removeItem('property_sale_date')
          }
        }
      } catch {
        setPropertySaleDate(null)
        setRelinquishedClosingDate(null)
      }
    }
  }, [currentExchange?.id, user?.id])

  // Seed mock exchange data for demo@open1031.ai user
  useEffect(() => {
    if (!user) return
    
    // Handle demo1 user - initialize fresh demo state for end-to-end demo
    if (user.email === 'demo1@open1031.ai') {
      const exchangeId = currentExchange?.id || user.id || 'demo1'
      const key1 = `exchange_wizard_v1_${exchangeId}_initial`
      const key2 = `sale_wizard_v1_${exchangeId}_initial`
      
      // Initialize demo data if not already present
      const existingData1 = localStorage.getItem(key1)
      const existingData2 = localStorage.getItem(key2)
      
      // Only clear signed agreements and submitted flags if we're initializing fresh demo data
      if (!existingData1 && !existingData2) {
        // Clear any existing signed agreements to start fresh
        localStorage.removeItem('exchange_agreement_signed')
        localStorage.removeItem('exchange_agreement_data')
        localStorage.removeItem('exchange_agreement_signed_date')
        localStorage.removeItem('exchange_agreement_signed_by')
        localStorage.removeItem('w9_signed')
        localStorage.removeItem('w9_signature')
        localStorage.removeItem('w9_signed_date')
        localStorage.removeItem('w9_signed_by')
        localStorage.removeItem('sales_hub_submitted')
        localStorage.removeItem('sales_hub_submitted_date')
        
        import('../lib/demoInit').then(({ initializeDemoUser }) => {
          initializeDemoUser(String(user.id))
        })
      }
      
      // Ensure demo data persistence is set up
      if (user.firstName && user.lastName) {
        dataPersistence.merge({
          firstName: user.firstName,
          lastName: user.lastName,
          phone: user.phone || '',
          ssn: user.ssn || '',
          address: user.address || {
            street: '1234 Maple Avenue',
            city: 'Los Angeles',
            state: 'CA',
            zipCode: '90001'
          }
        })
      }
      
      return
    }
    
    // Handle fresh demo user - initialize empty state
    if (user.email === 'demofresh@open1031.ai') {
      const exchangeId = currentExchange?.id || user.id || 'demo-fresh'
      const key = `exchange_wizard_v1_${exchangeId}_initial`
      const existingData = localStorage.getItem(key)
      
      if (!existingData) {
        import('../lib/demoInit').then(({ initializeDemoUser }) => {
          initializeDemoUser(String(user.id))
        })
      }
      localStorage.removeItem('exchange_agreement_signed')
      localStorage.removeItem('exchange_agreement_data')
      localStorage.removeItem('exchange_agreement_signed_date')
      localStorage.removeItem('exchange_agreement_signed_by')
      return
    }
    
    if (user.email === 'demo1@open1031.ai') {
      const wasJustReset = localStorage.getItem('demo_reset_just_completed') === 'true'
      if (wasJustReset) {
        localStorage.removeItem('demo_reset_just_completed')
        import('../lib/demoInit').then(({ initializeDemoUser }) => {
          initializeDemoUser(String(user.id))
        })
      }
    }
    
    // Don't seed mock Sales Hub data - data should only populate after Neela extraction
    // Only profile data should be prefilled (handled in SaleWizard component)
  }, [user, currentExchange?.id])

  // Auto-open Sales Hub for demo1 user after identity verification on login
  useEffect(() => {
    if (!user || user.email !== 'demo1@open1031.ai') return
    if (!isIdentityVerified) return
    
    const hasShownSalesHubKey = `sales_hub_shown_demo1_session`
    const hasShownSalesHub = sessionStorage.getItem(hasShownSalesHubKey) === 'true'
    
    if (!hasShownSalesHub && !isWizardOpen) {
      setTimeout(() => {
        setIsWizardOpen(true)
        setWizardInitialTab('initial')
        sessionStorage.setItem(hasShownSalesHubKey, 'true')
      }, 500)
    }
  }, [user, isIdentityVerified, isWizardOpen])

  // Auto-open hub for new users who haven't completed KYC or started an exchange
  useEffect(() => {
    if (!user) return
    if (user.email === 'demo1@open1031.ai') return // Skip for demo1 - handled above
    
    // Check if we've already shown the KYC modal for this user (persist across sessions)
    const kycModalShownKey = `kyc_modal_shown_${user.id}`
    const hasShownKYCModal = localStorage.getItem(kycModalShownKey) === 'true'
    
    if (hasCheckedNewUserRef.current || hasShownKYCModal) return
    
    hasCheckedNewUserRef.current = true
    
    // Check if user has started an exchange (has wizard data)
    const exchangeId = currentExchange?.id || user.id || 'anon'
    const key1 = `exchange_wizard_v1_${exchangeId}_initial`
    const key2 = `sale_wizard_v1_${exchangeId}_initial`
    const wizardData1 = localStorage.getItem(key1)
    const wizardData2 = localStorage.getItem(key2)
    const wizardData = wizardData2 || wizardData1
    
    // Parse wizard data to check if it's actually filled out
    let hasWizardData = false
    if (wizardData) {
      try {
        const parsed = JSON.parse(wizardData)
        hasWizardData = !!(parsed?.relinquished?.addressOrLegalDescription || parsed?.exchangerPhaseOne?.names || parsed?.relinquished?.closingDate)
      } catch {}
    }
    
    // Check if user has any saved progress or activities
    const activities = activityLog.getActivities()
    const hasActivities = activities.length > 0
    
    // If user is new and not identity verified and has no data, show KYC modal
    if (!isIdentityVerified && !hasWizardData && !hasActivities) {
      // Mark that we've shown the KYC modal for this user
      localStorage.setItem(kycModalShownKey, 'true')
      setTimeout(() => {
        setShowKYCModal(true)
        toast('Welcome! Let\'s verify your identity to get started.', { icon: '👋' })
      }, 500)
    }
  }, [user, isIdentityVerified, currentExchange?.id])

  const loadRelinquishedPropertyData = React.useCallback(() => {
    try {
      const exchangeId = currentExchange?.id || user?.id || 'anon'
      // Check all storage keys for compatibility (phase1 takes priority as it's the most recent)
      const key1 = `exchange_wizard_v1_${exchangeId}_initial`
      const key2 = `sale_wizard_v1_${exchangeId}_initial`
      const key3 = `sale_wizard_v1_${exchangeId}_phase1`
      const raw1 = localStorage.getItem(key1)
      const raw2 = localStorage.getItem(key2)
      const raw3 = localStorage.getItem(key3)
      const raw = raw3 || raw2 || raw1
      const storageKey = raw3 ? key3 : (raw2 ? key2 : key1)
      
      if (raw) {
        const parsed = JSON.parse(raw)
        // Only load data if Neela has extracted it (hasExtractedWithNeela must be true)
        const hasExtractedWithNeela = parsed?.hasExtractedWithNeela === true
        
        // Check if Sales Hub has meaningful data (user has filled out fields)
        const hasSalesHubData = !!(parsed?.relinquished?.addressOrLegalDescription?.trim() || 
                                  parsed?.exchangerPhaseOne?.names?.trim() ||
                                  parsed?.relinquished?.closingDate?.trim())
        
        if (!hasExtractedWithNeela || !hasSalesHubData) {
          // Don't load Sales Hub data until Neela has extracted it AND Sales Hub has data
          return
        }
        
        // Only load data if user has actually started the process
        // Check for non-empty values (not just truthy, since empty strings are falsy)
        const addressOrDesc = parsed?.relinquished?.addressOrLegalDescription || parsed?.relinquished?.address || parsed?.relinquishedProperty?.addressOrLegalDescription
        const exchangerNames = parsed?.exchangerPhaseOne?.names || parsed?.exchangers?.[0]?.names
        const closingDate = parsed?.relinquished?.closingDate || parsed?.relinquishedProperty?.closingDate
        const hasData = (addressOrDesc && addressOrDesc.trim() !== '') || 
                       (exchangerNames && exchangerNames.trim() !== '') || 
                       (closingDate && closingDate.trim() !== '')
        
        if (hasData) {
          setHasStartedProcess(true)
          
          // Parse address from addressOrLegalDescription format: "123 Street, City, State ZIP"
          if (addressOrDesc) {
            const addressParts = addressOrDesc.split(',').map((p: string) => p.trim())
            if (addressParts.length >= 3) {
              setRelinquishedAddress(addressParts[0])
              setRelinquishedCity(addressParts[1])
              const stateZip = addressParts[2].split(' ')
              if (stateZip.length >= 2) {
                setRelinquishedStateCode(stateZip[0])
                setRelinquishedZip(stateZip.slice(1).join(' '))
              }
            } else if (addressParts.length === 1) {
              setRelinquishedAddress(addressParts[0])
            }
          } else {
            // Fallback to old format
            const rel = parsed?.relinquished || parsed?.relinquishedProperty
            if (rel?.address) setRelinquishedAddress(rel.address)
            if (rel?.city) setRelinquishedCity(rel.city)
            if (rel?.state) setRelinquishedStateCode(rel.state)
            if (rel?.zip) setRelinquishedZip(rel.zip)
          }
          
          // Check for closing date in both formats (relinquished and relinquishedProperty)
          const finalClosingDate = parsed?.relinquished?.closingDate || parsed?.relinquishedProperty?.closingDate
          if (finalClosingDate && finalClosingDate.trim() !== '') {
            setRelinquishedClosingDate(finalClosingDate)
            // For demo1 user: Phase 3 should only start when Sales Hub is started AND closing date is set
            // This ensures Phase 3 shows as "not started" after reset until both conditions are met
            const isDemo1 = user?.email === 'demo1@open1031.ai'
            const hasSalesHubStarted = !!(addressOrDesc || exchangerNames)
            
            const closingDate = new Date(finalClosingDate)
            if (!isNaN(closingDate.getTime())) {
              // For demo1: only set propertySaleDate if Sales Hub is started (has data) - ensures Phase 3 remains "not started" until Sales Hub is started
              // For other users: always set when closing date exists
              if (!isDemo1 || hasSalesHubStarted) {
                setPropertySaleDate(closingDate)
                localStorage.setItem('property_sale_date', closingDate.toISOString())
              } else if (isDemo1) {
                // For demo1: don't set propertySaleDate if Sales Hub hasn't started yet
                setPropertySaleDate(null)
              }
            }
          }
          
          // For demo user, set mock property details
          if (isDemoUser && (addressOrDesc?.includes('123 Oak') || parsed?.relinquished?.address === '123 Oak Street')) {
            setRelinquishedImage('https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1200&q=80&auto=format&fit=crop')
            setRelinquishedPrice('$1,048,000')
            setRelinquishedBeds(3)
            setRelinquishedBaths(3)
            setRelinquishedSqft(1628)
            setZillowUrl('https://www.zillow.com/homes/123-Oak-Street-Los-Angeles-CA-90001')
            setRedfinUrl('https://www.redfin.com/stingray/do/location-autocomplete?location=123%20Oak%20Street%2C%20Los%20Angeles%2C%20CA%2090001')
          }
        } else {
          // No meaningful data found - reset to not started
          setHasStartedProcess(false)
          setPropertySaleDate(null)
          setRelinquishedClosingDate(null)
          setRelinquishedAddress('')
          setRelinquishedCity('')
          setRelinquishedStateCode('')
          setRelinquishedZip('')
          // For demo1: ensure propertySaleDate is cleared when no data exists
          if (user?.email === 'demo1@open1031.ai') {
            setPropertySaleDate(null)
          }
        }
      } else {
        // No wizard data at all - reset to not started
        setHasStartedProcess(false)
        setPropertySaleDate(null)
        setRelinquishedClosingDate(null)
        setRelinquishedAddress('')
        setRelinquishedCity('')
        setRelinquishedStateCode('')
        setRelinquishedZip('')
        // For demo1: ensure propertySaleDate is cleared when no wizard data exists
        if (user?.email === 'demo1@open1031.ai') {
          setPropertySaleDate(null)
        }
      }
    } catch (error) {
      console.error('Failed to load property data:', error)
      // On error, reset to not started
      setHasStartedProcess(false)
      setPropertySaleDate(null)
    }
  }, [currentExchange?.id, user?.id, user?.email])

  useEffect(() => {
    loadRelinquishedPropertyData()
  }, [loadRelinquishedPropertyData])

  // Listen for wizard data updates
  useEffect(() => {
    const handleDataUpdate = (event: CustomEvent) => {
      if (event.detail?.scope === 'initial') {
        loadRelinquishedPropertyData()
      }
    }
    
    window.addEventListener('wizardDataUpdated', handleDataUpdate as EventListener)
    return () => {
      window.removeEventListener('wizardDataUpdated', handleDataUpdate as EventListener)
    }
  }, [loadRelinquishedPropertyData])

  // Fetch from property API when address changes (skip for demo user with mock data)
  useEffect(() => {
    // Skip API call for demo user with mock property
    if (isDemoUser && relinquishedAddress === '123 Oak Street') {
      return
    }
    
    let cancelled = false
    const run = async () => {
      if (!relinquishedAddress) return
      try {
        const data = await propertyApi.lookupByAddress(relinquishedAddress)
        if (cancelled) return
        setRelinquishedImage(data.imageUrl)
        setRelinquishedPrice(typeof data.price === 'number' ? `$${data.price.toLocaleString()}` : (data.price || null))
        setRelinquishedBeds(data.beds)
        setRelinquishedBaths(data.baths)
        setRelinquishedSqft(data.sqft)
        setZillowUrl(data.zillowUrl)
        setRedfinUrl(data.redfinUrl)
      } catch (e) {
        // leave defaults
      }
    }
    run()
    return () => { cancelled = true }
  }, [relinquishedAddress, user?.email])

  // Calculate deadlines based on sale date (only if process has started)
  const identificationDeadline = propertySaleDate ? new Date(propertySaleDate) : null
  if (identificationDeadline) {
    identificationDeadline.setDate(identificationDeadline.getDate() + 45)
  }
  
  const exchangeDeadline = propertySaleDate ? new Date(propertySaleDate) : null
  if (exchangeDeadline) {
    exchangeDeadline.setDate(exchangeDeadline.getDate() + 180)
  }
  
  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    })
  }
  
  // Determine current phase (only if process has started)
  // Only consider property sold if we have both a sale date AND meaningful wizard data
  // Use useMemo to recalculate when dependencies change
  const { hasMeaningfulWizardData, totalDaysFromSale, isPropertySold, daysUntilSale, idDaysRemaining, exchangeDaysRemaining, totalDaysElapsed } = useMemo(() => {
    const exchangeId = currentExchange?.id || user?.id || 'anon'
    const key1 = `exchange_wizard_v1_${exchangeId}_initial`
    const key2 = `sale_wizard_v1_${exchangeId}_initial`
    const key3 = `sale_wizard_v1_${exchangeId}_phase1`
    const raw1 = localStorage.getItem(key1)
    const raw2 = localStorage.getItem(key2)
    const raw3 = localStorage.getItem(key3)
    const raw = raw3 || raw2 || raw1
    let hasMeaningfulWizardData = false
    if (raw) {
      try {
        const parsed = JSON.parse(raw)
        const addressOrDesc = parsed?.relinquished?.addressOrLegalDescription || parsed?.relinquished?.address || parsed?.relinquishedProperty?.addressOrLegalDescription
        const exchangerNames = parsed?.exchangerPhaseOne?.names || parsed?.exchangers?.[0]?.names
        const closingDate = parsed?.relinquished?.closingDate || parsed?.relinquishedProperty?.closingDate
        hasMeaningfulWizardData = (addressOrDesc && addressOrDesc.trim() !== '') || 
                                 (exchangerNames && exchangerNames.trim() !== '') ||
                                 (parsed?.buyer?.names && parsed.buyer.names.trim() !== '') ||
                                 (parsed?.closingCompanySale?.name && parsed.closingCompanySale.name.trim() !== '') ||
                                 (closingDate && closingDate.trim() !== '')
      } catch {}
    }
    
    const totalDaysFromSale = propertySaleDate && hasMeaningfulWizardData ? Math.floor((currentTime.getTime() - propertySaleDate.getTime()) / (1000 * 60 * 60 * 24)) : null
    const isPropertySold = totalDaysFromSale !== null && totalDaysFromSale >= 0
    const daysUntilSale = propertySaleDate && hasMeaningfulWizardData && totalDaysFromSale !== null ? Math.abs(Math.min(0, totalDaysFromSale as number)) : 0
    const idDaysRemaining = propertySaleDate && hasMeaningfulWizardData && isPropertySold && totalDaysFromSale !== null ? Math.max(0, 45 - totalDaysFromSale) : 0
    // For demo1: return null if propertySaleDate is not set (to show "Not Started")
    // For others: return 0 if not started
    const isDemo1 = user?.email === 'demo1@open1031.ai'
    const exchangeDaysRemaining = propertySaleDate && hasMeaningfulWizardData && isPropertySold && totalDaysFromSale !== null 
      ? Math.max(0, 180 - totalDaysFromSale) 
      : (isDemo1 ? null : 0)
    const totalDaysElapsed = propertySaleDate && hasMeaningfulWizardData && isPropertySold && totalDaysFromSale !== null ? Math.min(180, totalDaysFromSale) : 0
    
    return { hasMeaningfulWizardData, totalDaysFromSale, isPropertySold, daysUntilSale, idDaysRemaining, exchangeDaysRemaining, totalDaysElapsed }
  }, [propertySaleDate, currentTime, currentExchange?.id, user?.id])
  // Cap progress at 95% until actually complete to show it's still in progress
  const overallProgress = isIdentityVerified && propertySaleDate && isPropertySold ? Math.min(95, Math.round((totalDaysElapsed / 180) * 100)) : 0
  
  
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = new Date(e.target.value)
    if (!isNaN(newDate.getTime())) {
      setPropertySaleDate(newDate)
    }
  }
  
  const formatDateForInput = (date: Date | null): string => {
    if (!date) return ''
    return date.toISOString().split('T')[0]
  }
  
  // Format timestamp helper
  const formatTimestamp = (date: Date): string => {
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
    
    if (diffDays === 0) return 'Today'
    if (diffDays === 1) return 'Yesterday'
    if (diffDays < 7) return `${diffDays} days ago`
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  // Recent activities - use real activities from activityLog, or mock data for demo user
  const getIconForActivityType = (type: string) => {
    switch (type) {
      case 'document': return FileCheck
      case 'property': return Building2
      case 'verification': return CheckCircle
      case 'hub': return LayoutDashboard
      case 'form': return FileText
      default: return CheckCircle
    }
  }
  
  const allActivities = (user?.email === 'demo@open1031.ai' && userActivities.length === 0 && hasStartedProcess) ? [
    {
      id: '1',
      type: 'document',
      title: 'Exchange Agreement Signed',
      description: 'You signed the exchange agreement',
      timestamp: formatTimestamp(new Date(Date.now() - 2 * 24 * 60 * 60 * 1000)),
      status: 'completed' as const,
      icon: FileCheck
    },
    {
      id: '2',
      type: 'property',
      title: 'Relinquished Property Added',
      description: '123 Oak Street, Los Angeles, CA 90001',
      timestamp: formatTimestamp(new Date(Date.now() - 5 * 24 * 60 * 60 * 1000)),
      status: 'completed' as const,
      icon: Building2
    },
    {
      id: '3',
      type: 'verification',
      title: 'Identity Verified',
      description: 'Your identity verification was completed',
      timestamp: formatTimestamp(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)),
      status: 'completed' as const,
      icon: CheckCircle
    },
    {
      id: '4',
      type: 'property',
      title: 'Replacement Property Identified',
      description: '456 Pine Avenue, Los Angeles, CA 90002',
      timestamp: formatTimestamp(new Date(Date.now() - 10 * 24 * 60 * 60 * 1000)),
      status: 'completed' as const,
      icon: MapPin
    }
  ] : userActivities.map(activity => ({
    id: activity.id,
    type: activity.type,
    title: activity.title,
    description: activity.description,
    timestamp: formatTimestamp(new Date(activity.timestamp)),
    status: activity.status,
    icon: getIconForActivityType(activity.type)
  }))

  const recentActivities = allActivities.slice(0, 10)

  const totalPages = Math.ceil(allActivities.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedActivities = allActivities.slice(startIndex, endIndex)

  // Calculate exchange progress based on hub completion
  // Main phases: Sales (33%), Identification (33%), Purchase (34%) = 100%
  const hubBasedProgress = React.useMemo(() => {
    if (!isIdentityVerified) return 0
    
    let progress = 0
    
    if (salesAccess.isComplete) {
      progress += 33
    } else if (salesAccess.completionPercent > 0) {
      progress += Math.round((salesAccess.completionPercent / 100) * 33)
    }
    
    if (identificationAccess.canAccess) {
      if (identificationAccess.isComplete) {
        progress += 33
      } else if (identificationAccess.completionPercent > 0) {
        progress += Math.round((identificationAccess.completionPercent / 100) * 33)
      }
    }
    
    if (purchaseAccess.canAccess) {
      if (purchaseAccess.isComplete) {
        progress += 34
      } else if (purchaseAccess.completionPercent > 0) {
        progress += Math.round((purchaseAccess.completionPercent / 100) * 34)
      }
    }
    
    return progress
  }, [
    isIdentityVerified,
    salesAccess.isComplete,
    salesAccess.completionPercent,
    identificationAccess.canAccess,
    identificationAccess.isComplete,
    identificationAccess.completionPercent,
    purchaseAccess.canAccess,
    purchaseAccess.isComplete,
    purchaseAccess.completionPercent
  ])
  
  const timelineProgressPercent = isIdentityVerified ? hubBasedProgress : 0
  const timelineStatusLabel = isIdentityVerified && propertySaleDate
    ? (isPropertySold ? `Day ${totalDaysElapsed} of 180` : `${daysUntilSale} days until start`)
    : 'Not Started'

  // Stats - show for demo users or if process has started
  const stats = (hasStartedProcess || isDemoUser || isIdentityVerified) ? [
    {
      name: 'Exchange Progress',
      value: `${hubBasedProgress}%`,
      icon: TrendingUp,
      color: 'text-blue-600 dark:text-blue-400'
    },
    {
      name: 'Documents Uploaded',
      value: uploadedDocsCount > 0 ? `${uploadedDocsCount}` : '—',
      icon: FileText,
      color: 'text-green-600 dark:text-green-400'
    },
    {
      name: 'Days Remaining',
      value: exchangeDaysRemaining !== null && exchangeDaysRemaining > 0 ? exchangeDaysRemaining.toString() : (exchangeDaysRemaining === null ? '—' : '0'),
      icon: Clock,
      color: 'text-orange-600 dark:text-orange-400'
    },
    {
      name: 'Estimated Savings',
      value: calculatorSavings || '—',
      icon: DollarSign,
      color: 'text-purple-600 dark:text-purple-400'
    }
  ] : []

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="bg-white dark:bg-slate-800/60 rounded-lg shadow-sm dark:shadow-slate-900/50 border border-secondary-200 dark:border-slate-700/50 p-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold text-secondary-900 dark:text-slate-100 flex items-center gap-3">
              <LayoutDashboard className="h-7 w-7 text-primary-600 dark:text-primary-400" />
              Welcome, {user?.firstName}
            </h1>
            <p className="text-secondary-600 dark:text-slate-400 mt-1">
              Here's an overview of your 1031 exchange progress
            </p>
          </div>
        </div>
      </div>

      {/* Identity Verification - Top Section */}
      {!isIdentityVerified && (
        <Card className="p-6 border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/20">
          <div className="flex items-start space-x-3">
            <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                Identity Verification Required
              </h3>
              <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                Complete your identity verification to access all platform features and begin your 1031 exchange.
              </p>
              <div className="mt-3">
                <Button 
                  size="sm" 
                  className="bg-yellow-600 hover:bg-yellow-700 dark:bg-yellow-600 dark:hover:bg-yellow-700"
                  onClick={() => setShowKYCModal(true)}
                >
                  <Shield className="h-4 w-4 mr-2" />
                  Verify Identity
                </Button>
              </div>
            </div>
          </div>
        </Card>
      )}

      {isIdentityVerified && nextStep && nextStep.hub !== 'kyc' && (
        <Card className="p-6 border-green-200 dark:border-green-800 bg-gradient-to-r from-green-50 to-[#69adba]/10 dark:from-green-900/20 dark:to-[#69adba]/20">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-center space-x-3">
              <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-sm font-medium text-green-800 dark:text-green-200">
                  Identity Verified
                </h3>
                <p className="text-sm text-green-700 dark:text-green-300 mt-0.5">
                  Great! Your identity has been verified. Ready to continue?
                </p>
              </div>
            </div>
            <Button
              onClick={nextStep.action}
              className="bg-gradient-to-r from-[#69adba] to-blue-500 hover:from-[#5a9aa6] hover:to-blue-600 dark:from-[#69adba] dark:to-blue-500 dark:hover:from-[#5a9aa6] dark:hover:to-blue-600 text-white shadow-lg border-0 px-6 py-3 text-base font-medium w-full md:w-auto"
            >
              <Sparkles className="h-5 w-5 mr-2" />
              Next Step: {nextStep.label}
              <ArrowRight className="h-5 w-5 ml-2" />
            </Button>
          </div>
        </Card>
      )}

      {isIdentityVerified && !nextStep && (
        <Card className="p-4 border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20">
          <div className="flex items-center space-x-3">
            <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-green-800 dark:text-green-200">
                All Hubs Complete
              </h3>
              <p className="text-sm text-green-700 dark:text-green-300 mt-0.5">
                Congratulations! You've completed all required exchange hubs.
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Stats Grid */}
      {stats.length > 0 && (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {stats.map((stat) => {
            const Icon = stat.icon
            return (
              <Card key={stat.name} className="p-6 dark:bg-slate-800 dark:border-slate-700">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <Icon className={`h-8 w-8 ${stat.color}`} />
                  </div>
                  <div className="ml-4 flex-1 min-w-0">
                    <p className="text-sm font-medium text-secondary-600 dark:text-slate-400 break-words">
                      {stat.name}
                    </p>
                    <p className="text-2xl font-semibold text-secondary-900 dark:text-slate-100 mt-1 break-words">
                      {stat.value}
                    </p>
                  </div>
                </div>
              </Card>
            )
          })}
        </div>
      )}

      {/* Exchange Timeline */}
      <Card className="p-6">
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-secondary-900 dark:text-slate-100">1031 Exchange Timeline</h2>
            <p className="text-sm text-secondary-600 dark:text-slate-400 mt-1">
              Track your progress through the 180-day exchange period
            </p>
          </div>

          {/* Unified Modern Timeline */}
          <div className="relative pt-8 pb-8">
            {/* Overall Progress Info */}
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <span className="text-sm font-semibold text-secondary-700 dark:text-slate-300">
                  Overall Exchange Progress
                </span>
                <span className="text-sm font-bold text-primary-600 dark:text-primary-400">
                  {timelineProgressPercent}%
                </span>
              </div>
              <span className="text-sm font-semibold text-secondary-700 dark:text-slate-300">
                {timelineStatusLabel}
              </span>
            </div>

            {/* Timeline Track */}
            <div className="absolute top-20 left-0 right-0 h-1.5 bg-slate-200/30 dark:bg-slate-700/20 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-green-500 via-blue-500 to-purple-500 dark:from-green-400 dark:via-blue-400 dark:to-purple-400 transition-all duration-1000 ease-out rounded-full shadow-lg"
                style={{ width: `${timelineProgressPercent}%` }}
              />
            </div>

              {/* Timeline Nodes and Cards */}
              <div className="relative grid grid-cols-3 gap-8 items-start">
                {/* Phase 1: Property Sale + Sales Hub */}
                <div className="flex flex-col items-center w-full">
                  {/* Timeline Node */}
                  <div className="relative z-20 mb-6">
                    <div className={`w-20 h-20 rounded-full flex items-center justify-center transition-all duration-300 ${
                      !isIdentityVerified
                        ? 'bg-slate-200 dark:bg-slate-800/50 shadow-lg ring-4 ring-slate-100 dark:ring-slate-900/30'
                        : salesAccess.isComplete
                        ? 'bg-green-500 dark:bg-green-600 shadow-2xl shadow-green-500/50 dark:shadow-green-500/30 ring-4 ring-green-200 dark:ring-green-900/50' 
                        : 'bg-green-500 dark:bg-green-600 shadow-2xl shadow-green-500/50 dark:shadow-green-500/30 ring-4 ring-green-200 dark:ring-green-900/50 animate-pulse-slow'
                    }`}>
                      <div className="w-12 h-12 rounded-full bg-white/20 dark:bg-white/10 flex items-center justify-center">
                        <CheckCircle className={`h-7 w-7 ${isIdentityVerified ? 'text-white' : 'text-slate-500 dark:text-slate-400'}`} />
                      </div>
                    </div>
                    <div className={`absolute -top-2 -right-2 w-6 h-6 rounded-full flex items-center justify-center shadow-lg ${
                      isIdentityVerified ? 'bg-green-500 dark:bg-green-600' : 'bg-slate-300 dark:bg-slate-700'
                    }`}>
                      <span className="text-xs font-black text-white">1</span>
                    </div>
                  </div>
                  
                  {/* Phase Label */}
                  <div className="text-center mb-6">
                    <p className="text-sm font-bold text-green-600 dark:text-green-400 uppercase tracking-wider mb-1">Phase 1</p>
                    <p className="text-xs text-secondary-600 dark:text-slate-400">Initial Setup</p>
                  </div>
                  
                  {/* Card */}
                  <div className="relative w-full py-3 rounded-xl bg-white dark:bg-slate-800 border-2 border-green-200/50 dark:border-green-800/30 transition-all duration-300 flex flex-col">
                    <div className="flex flex-col items-center text-center flex-1">
                      <h3 className="text-sm font-bold text-secondary-900 dark:text-slate-100 mb-0.5">
                        Sell Relinquished Property
                      </h3>
                      <p className="text-xs text-secondary-600 dark:text-slate-400 mb-2">
                        {!isIdentityVerified
                          ? 'Not started'
                          : isPropertySold
                          ? 'Property sold'
                          : salesAccess.isComplete && daysUntilSale > 0
                          ? 'Property under contract'
                          : !salesAccess.canAccess
                          ? 'Sell Relinquished Property'
                          : 'In progress'}
                      </p>
                      {isIdentityVerified && propertySaleDate && (
                        <div className="flex items-center justify-center space-x-1 text-xs text-secondary-500 dark:text-slate-500 mb-2">
                          <Calendar className="h-3 w-3" />
                          <span className="font-medium">{formatDate(propertySaleDate)}</span>
                        </div>
                      )}
                      {!isIdentityVerified ? (
                        <div className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 text-xs font-semibold">
                          Not Started
                        </div>
                      ) : !salesAccess.canAccess ? (
                        <div className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 text-xs font-semibold">
                          Not Started
                        </div>
                      ) : propertySaleDate ? (
                        daysUntilSale > 0 ? (
                          <div className="inline-flex items-center px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 text-xs font-semibold">
                            Under Contract
                          </div>
                        ) : (
                          <div className="inline-flex items-center px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 text-xs font-semibold">
                            <CheckCircle className="h-2.5 w-2.5 mr-1" />
                            Sold
                          </div>
                        )
                      ) : (
                        <div className="inline-flex items-center px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 text-xs font-semibold">
                          <Clock className="h-2.5 w-2.5 mr-1" />
                          In Progress
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Phase 2: 45-Day Identification + Identification Hub */}
                <div className="flex flex-col items-center w-full">
                  {/* Timeline Node */}
                  <div className="relative z-20 mb-6">
                    <div className={`w-20 h-20 rounded-full flex items-center justify-center transition-all duration-300 ${
                      !isIdentityVerified
                        ? 'bg-slate-200 dark:bg-slate-800/50 shadow-lg ring-4 ring-slate-100 dark:ring-slate-900/30'
                        : identificationAccess.isComplete
                        ? 'bg-green-500 dark:bg-green-600 shadow-2xl shadow-green-500/50 dark:shadow-green-500/30 ring-4 ring-green-200 dark:ring-green-900/50'
                        : salesAccess.isComplete && idDaysRemaining > 0
                        ? 'bg-blue-500 dark:bg-blue-600 shadow-2xl shadow-blue-500/50 dark:shadow-blue-500/30 ring-4 ring-blue-200 dark:ring-blue-900/50 animate-pulse-slow'
                        : 'bg-blue-200 dark:bg-blue-800/50 shadow-lg ring-4 ring-blue-100 dark:ring-blue-900/30'
                    }`}>
                      <div className="w-12 h-12 rounded-full bg-white/20 dark:bg-white/10 flex items-center justify-center">
                        <Clock className={`h-7 w-7 ${
                          !isIdentityVerified ? 'text-slate-500 dark:text-slate-400' : (identificationAccess.isComplete || (salesAccess.isComplete && idDaysRemaining > 0) ? 'text-white' : 'text-blue-600 dark:text-blue-300')
                        }`} />
                      </div>
                    </div>
                    <div className={`absolute -top-2 -right-2 w-6 h-6 rounded-full flex items-center justify-center shadow-lg ${
                      !isIdentityVerified
                        ? 'bg-slate-300 dark:bg-slate-700'
                        : identificationAccess.isComplete 
                        ? 'bg-green-500 dark:bg-green-600' 
                        : salesAccess.isComplete && idDaysRemaining > 0
                        ? 'bg-blue-500 dark:bg-blue-600'
                        : 'bg-blue-300 dark:bg-blue-700'
                    }`}>
                      <span className="text-xs font-black text-white">2</span>
                    </div>
                  </div>
                  
                  {/* Phase Label */}
                  <div className="text-center mb-6">
                    <p className={`text-sm font-bold uppercase tracking-wider mb-1 ${
                      identificationAccess.isComplete 
                        ? 'text-green-600 dark:text-green-400' 
                        : salesAccess.isComplete && idDaysRemaining > 0
                        ? 'text-blue-600 dark:text-blue-400'
                        : 'text-blue-400 dark:text-blue-500'
                    }`}>Phase 2</p>
                    <p className="text-xs text-secondary-600 dark:text-slate-400">During Exchange</p>
                  </div>
                  
                  {/* Card */}
                  <div className="relative w-full py-3 rounded-xl bg-white dark:bg-slate-800 border-2 border-blue-200/50 dark:border-blue-800/30 transition-all duration-300 flex flex-col">
                    <div className="flex flex-col items-center text-center flex-1">
                      <h3 className="text-sm font-bold text-secondary-900 dark:text-slate-100 mb-0.5">
                        45-Day Identification
                      </h3>
                      <p className="text-xs text-secondary-600 dark:text-slate-400 mb-2">
                        Identify replacement property
                      </p>
                      {identificationDeadline && (
                        <div className="flex items-center justify-center space-x-1 text-xs text-secondary-500 dark:text-slate-500 mb-2">
                          <Calendar className="h-3 w-3" />
                          <span className="font-medium">{formatDate(identificationDeadline)}</span>
                        </div>
                      )}
                      {!isIdentityVerified ? (
                        <div className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 text-xs font-semibold">
                          Not Started
                        </div>
                      ) : identificationAccess.isComplete ? (
                        <div className="inline-flex items-center px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 text-xs font-semibold">
                          <CheckCircle className="h-2.5 w-2.5 mr-1" />
                          Completed
                        </div>
                      ) : isPropertySold && idDaysRemaining > 0 ? (
                        <div className="inline-flex flex-col items-center px-3 py-2 rounded-lg bg-gradient-to-br from-blue-100 to-blue-200 dark:from-blue-900/70 dark:to-blue-800/70 border-2 border-blue-300 dark:border-blue-600 shadow-md">
                          <div className="text-3xl font-black leading-none text-blue-700 dark:text-blue-100 tracking-tight">
                            {idDaysRemaining}
                          </div>
                          <div className="text-xs font-bold mt-1 text-blue-800 dark:text-blue-200 uppercase tracking-wider">
                            days remaining
                          </div>
                        </div>
                      ) : !isPropertySold && propertySaleDate ? (
                        <div className="inline-flex items-center px-3 py-1 rounded-lg bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 text-xl font-bold">
                          —
                        </div>
                      ) : (
                        <div className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 text-xs font-semibold">
                          Not Started
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Phase 3: 180-Day Exchange + Purchase/Disbursement/IRS Hubs */}
                <div className="flex flex-col items-center w-full">
                  {/* Timeline Node */}
                  <div className="relative z-20 mb-6">
                    <div className={`w-20 h-20 rounded-full flex items-center justify-center transition-all duration-300 ${
                      !isIdentityVerified
                        ? 'bg-slate-200 dark:bg-slate-800/50 shadow-lg ring-4 ring-slate-100 dark:ring-slate-900/30'
                        : hasStartedProcess && identificationAccess.isComplete && exchangeDaysRemaining !== null && exchangeDaysRemaining >= 0
                        ? 'bg-purple-500 dark:bg-purple-600 shadow-2xl shadow-purple-500/50 dark:shadow-purple-500/30 ring-4 ring-purple-200 dark:ring-purple-900/50 animate-pulse-slow'
                        : 'bg-purple-200 dark:bg-purple-800/50 shadow-lg ring-4 ring-purple-100 dark:ring-purple-900/30'
                    }`}>
                      <div className="w-12 h-12 rounded-full bg-white/20 dark:bg-white/10 flex items-center justify-center">
                        <Building2 className={`h-7 w-7 ${
                          !isIdentityVerified ? 'text-slate-500 dark:text-slate-400' : (hasStartedProcess && identificationAccess.isComplete && exchangeDaysRemaining !== null && exchangeDaysRemaining >= 0 ? 'text-white' : 'text-purple-600 dark:text-purple-300')
                        }`} />
                      </div>
                    </div>
                    <div className={`absolute -top-2 -right-2 w-6 h-6 rounded-full flex items-center justify-center shadow-lg ${
                      !isIdentityVerified
                        ? 'bg-slate-300 dark:bg-slate-700'
                        : hasStartedProcess && identificationAccess.isComplete && exchangeDaysRemaining !== null && exchangeDaysRemaining >= 0
                        ? 'bg-purple-500 dark:bg-purple-600'
                        : 'bg-purple-300 dark:bg-purple-700'
                    }`}>
                      <span className="text-xs font-black text-white">3</span>
                    </div>
                  </div>
                  
                  {/* Phase Label */}
                  <div className="text-center mb-6">
                    <p className={`text-sm font-bold uppercase tracking-wider mb-1 ${
                      hasStartedProcess && identificationAccess.isComplete && exchangeDaysRemaining !== null && exchangeDaysRemaining >= 0
                        ? 'text-purple-600 dark:text-purple-400'
                        : 'text-purple-400 dark:text-purple-500'
                    }`}>Phase 3</p>
                    <p className="text-xs text-secondary-600 dark:text-slate-400">Acquisition</p>
                  </div>
                  
                  {/* Card */}
                  <div className="relative w-full py-3 rounded-xl bg-white dark:bg-slate-800 border-2 border-purple-200/50 dark:border-purple-800/30 transition-all duration-300 flex flex-col">
                    <div className="flex flex-col items-center text-center flex-1">
                      <h3 className="text-sm font-bold text-secondary-900 dark:text-slate-100 mb-0.5">
                        180-Day Exchange
                      </h3>
                      <p className="text-xs text-secondary-600 dark:text-slate-400 mb-2">
                        Complete exchange
                      </p>
                      {exchangeDeadline && (
                        <div className="flex items-center justify-center space-x-1 text-xs text-secondary-500 dark:text-slate-500 mb-2">
                          <Calendar className="h-3 w-3" />
                          <span className="font-medium">{formatDate(exchangeDeadline)}</span>
                        </div>
                      )}
                      {!isIdentityVerified ? (
                        <div className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 text-xs font-semibold">
                          Not Started
                        </div>
                      ) : isPropertySold && exchangeDaysRemaining !== null && exchangeDaysRemaining > 0 && propertySaleDate ? (
                        <div className="inline-flex flex-col items-center px-3 py-2 rounded-lg bg-gradient-to-br from-purple-100 to-purple-200 dark:from-purple-900/70 dark:to-purple-800/70 border-2 border-purple-300 dark:border-purple-600 shadow-md">
                          <div className="text-3xl font-black leading-none text-purple-700 dark:text-purple-100 tracking-tight">
                            {exchangeDaysRemaining}
                          </div>
                          <div className="text-xs font-bold mt-1 text-purple-800 dark:text-purple-200 uppercase tracking-wider">
                            days remaining
                          </div>
                        </div>
                      ) : !isPropertySold && propertySaleDate ? (
                        <div className="inline-flex items-center px-3 py-1 rounded-lg bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 text-xl font-bold">
                          —
                        </div>
                      ) : (
                        <div className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 text-xs font-semibold">
                          Not Started
                        </div>
                      )}
                    </div>
                  </div>
              </div>
            </div>
          </div>

          {/* Warning Messages */}
          {exchangeDeadline && exchangeDaysRemaining !== null && exchangeDaysRemaining > 0 && exchangeDaysRemaining <= 14 && (
            <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start space-x-3">
              <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-semibold text-red-900 dark:text-red-200">Exchange Deadline Critical</h4>
                <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                  Only {exchangeDaysRemaining} days left to complete your exchange.
                  Final deadline: {formatDate(exchangeDeadline)}
                </p>
              </div>
            </div>
          )}

          {!propertySaleDate && (
            <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg flex items-start space-x-3">
              <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-200">Exchange Not Started</h4>
                <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                  Start your 1031 exchange process to begin tracking your timeline. The 180-day exchange period will begin once you enter your property sale date.
                </p>
              </div>
            </div>
          )}
        </Card>

        {/* Exchange Hubs Section */}
        <Card className="p-6">
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-secondary-900 dark:text-slate-100 flex items-center gap-2">
              <LayoutDashboard className="h-5 w-5 text-primary-600 dark:text-primary-400" />
              Exchange Hubs
            </h2>
          </div>
          <div className="grid grid-cols-3 gap-8 items-start">
            {/* Phase 1: Sales Hub */}
            <div className="flex flex-col items-center">
              <div className="text-center mb-4">
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-green-100 dark:bg-green-900/40 border-2 border-green-300 dark:border-green-700">
                  <span className="text-xs font-bold text-green-700 dark:text-green-300 uppercase tracking-wider">Phase 1</span>
                </div>
                <p className="text-xs text-secondary-600 dark:text-slate-400 mt-1.5">Initial Setup</p>
              </div>
              <div 
                onClick={() => {
                  if (!salesAccess.canAccess) {
                    setShowKYCModal(true)
                  } else {
                    setWizardInitialTab('initial')
                    setIsWizardOpen(true)
                  }
                }}
                className={`group relative w-full p-2 rounded-lg border-2 transition-all duration-300 ${
                  nextStep?.hub === 'sales'
                    ? 'border-green-600 dark:border-green-400 hover:border-green-700 dark:hover:border-green-500 shadow-lg shadow-green-600/20 dark:shadow-green-400/30 cursor-pointer bg-white dark:bg-slate-800 ring-2 ring-green-600/20 dark:ring-green-400/30 animate-pulse-slow'
                    : salesAccess.canAccess
                    ? 'border-green-200/50 dark:border-green-800/30 hover:border-green-400 dark:hover:border-green-600 hover:shadow-md cursor-pointer bg-white dark:bg-slate-800'
                    : 'border-slate-200 dark:border-slate-700 opacity-50 bg-slate-50 dark:bg-slate-800/20 cursor-pointer'
                }`}
              >
                {!salesAccess.canAccess && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white/5 dark:bg-slate-900/10 backdrop-blur-[0.5px] rounded-xl z-10">
                    <div className="text-center bg-white/85 dark:bg-slate-800/85 px-3 py-2 rounded-lg border-2 border-slate-300 dark:border-slate-600">
                      <Lock className="h-4 w-4 text-slate-600 dark:text-slate-300 mx-auto mb-1" />
                      <p className="text-xs text-slate-700 dark:text-slate-200 font-semibold">{salesAccess.message || 'Identity verification required'}</p>
                    </div>
                  </div>
                )}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-green-500/10 dark:bg-green-500/20">
                      <Layers className="h-4 w-4 text-green-600 dark:text-green-400" />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-secondary-900 dark:text-slate-100">Sales Hub</h4>
                      <p className="text-xs text-secondary-600 dark:text-slate-400">Relinquished property details</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {salesAccess.canAccess && nextStep?.hub !== 'sales' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          toggleHubLock('sales')
                        }}
                        className="p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                        title={isHubLocked('sales') ? 'Unlock hub' : 'Lock hub'}
                      >
                        {isHubLocked('sales') ? (
                          <Lock className="h-3.5 w-3.5 text-slate-500 dark:text-slate-400" />
                        ) : (
                          <Lock className="h-3.5 w-3.5 text-green-500 dark:text-green-400" />
                        )}
                      </button>
                    )}
                    {salesAccess.completionPercent === 100 ? (
                      <CheckCircle2 className="h-4 w-4 text-green-500 dark:text-green-400" />
                    ) : (
                      <div className="h-4 w-4 rounded-full border-2 border-green-300 dark:border-green-600 flex items-center justify-center">
                        <div className="h-1.5 w-1.5 rounded-full bg-green-500 dark:bg-green-400" />
                      </div>
                    )}
                  </div>
                </div>
                <div className="space-y-1.5">
                  <div className="h-2 bg-green-100 dark:bg-green-900/30 rounded-full overflow-hidden">
                    <div 
                      className="h-full transition-all duration-500 bg-green-600 dark:bg-green-400"
                      style={{ 
                        width: `${salesAccess.completionPercent}%`
                      }}
                    />
                  </div>
                  <div className="flex items-center justify-center">
                    <p className="text-xs font-semibold text-green-600 dark:text-green-400">
                      {salesAccess.completionPercent}%
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Phase 2: Identification Hub */}
            <div className="flex flex-col items-center">
              <div className="text-center mb-4">
                <div className={`inline-flex items-center px-3 py-1 rounded-full border-2 ${
                  identificationAccess.isComplete
                    ? 'bg-green-100 dark:bg-green-900/40 border-green-300 dark:border-green-700'
                    : salesAccess.isComplete && idDaysRemaining > 0
                    ? 'bg-blue-100 dark:bg-blue-900/40 border-blue-300 dark:border-blue-700'
                    : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
                }`}>
                  <span className={`text-xs font-bold uppercase tracking-wider ${
                    identificationAccess.isComplete
                      ? 'text-green-700 dark:text-green-300'
                      : salesAccess.isComplete && idDaysRemaining > 0
                      ? 'text-blue-700 dark:text-blue-300'
                      : 'text-blue-500 dark:text-blue-400'
                  }`}>Phase 2</span>
                </div>
                <p className="text-xs text-secondary-600 dark:text-slate-400 mt-1.5">During Exchange</p>
              </div>
              <div 
                onClick={() => identificationAccess.canAccess && setIsExchangeHubOpen(true)}
                className={`group relative w-full p-2 rounded-lg border-2 transition-all duration-300 ${
                  nextStep?.hub === 'identification'
                    ? 'border-blue-600 dark:border-blue-400 hover:border-blue-700 dark:hover:border-blue-500 shadow-lg shadow-blue-600/20 dark:shadow-blue-400/30 cursor-pointer bg-white dark:bg-slate-800 ring-2 ring-blue-600/20 dark:ring-blue-400/30 animate-pulse-slow'
                    : identificationAccess.canAccess
                    ? 'border-blue-200/50 dark:border-blue-800/30 hover:border-blue-400 dark:hover:border-blue-600 hover:shadow-md cursor-pointer bg-white dark:bg-slate-800'
                    : 'border-slate-200 dark:border-slate-700 opacity-50 bg-slate-50 dark:bg-slate-800/20'
                }`}
              >
                {!identificationAccess.canAccess && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white/5 dark:bg-slate-900/10 backdrop-blur-[0.5px] rounded-xl z-10 pointer-events-none">
                    <div className="text-center bg-white/85 dark:bg-slate-800/85 px-3 py-2 rounded-lg border-2 border-slate-300 dark:border-slate-600">
                      <Lock className="h-4 w-4 text-slate-600 dark:text-slate-300 mx-auto mb-1" />
                      <p className="text-xs text-slate-700 dark:text-slate-200 font-semibold">{identificationAccess.message || 'Please complete the Sales Hub first'}</p>
                    </div>
                  </div>
                )}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-blue-500/10 dark:bg-blue-500/20">
                      <FileCheck className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-secondary-900 dark:text-slate-100">Identification Hub</h4>
                      <p className="text-xs text-secondary-600 dark:text-slate-400">Identify replacement properties</p>
                    </div>
                  </div>
                  {identificationAccess.completionPercent === 100 ? (
                    <CheckCircle2 className="h-4 w-4 text-green-500 dark:text-green-400" />
                  ) : identificationAccess.canAccess ? (
                    <div className="h-4 w-4 rounded-full border-2 border-blue-300 dark:border-blue-600 flex items-center justify-center">
                      <div className="h-1.5 w-1.5 rounded-full bg-blue-500 dark:bg-blue-400" />
                    </div>
                  ) : null}
                </div>
                <div className="space-y-1.5">
                  <div className="h-2 bg-blue-100 dark:bg-blue-900/30 rounded-full overflow-hidden">
                    <div 
                      className="h-full transition-all duration-500"
                      style={{ 
                        width: `${identificationAccess.completionPercent}%`,
                        background: 'linear-gradient(to right, #69adba, #8bc6d0, #69adba)'
                      }}
                    />
                  </div>
                  <p className="text-xs font-medium text-[#69adba] dark:text-[#69adba]">{identificationAccess.completionPercent}% complete</p>
                </div>
                {identificationAccess.canAccess && nextStep?.hub !== 'identification' && (
                  <div className="absolute top-2 right-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        toggleHubLock('identification')
                      }}
                      className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                      title={isHubLocked('identification') ? 'Unlock hub' : 'Lock hub'}
                    >
                      {isHubLocked('identification') ? (
                        <Lock className="h-4 w-4 text-slate-500 dark:text-slate-400" />
                      ) : (
                        <Lock className="h-4 w-4 text-blue-500 dark:text-blue-400" />
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Phase 3: Purchase/Disbursement Hubs */}
            <div className="flex flex-col items-center gap-4">
              <div className="text-center mb-4 w-full">
                <div className={`inline-flex items-center px-3 py-1 rounded-full border-2 ${
                  hasStartedProcess && identificationAccess.isComplete && exchangeDaysRemaining !== null && exchangeDaysRemaining >= 0
                    ? 'bg-purple-100 dark:bg-purple-900/40 border-purple-300 dark:border-purple-700'
                    : 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800'
                }`}>
                  <span className={`text-xs font-bold uppercase tracking-wider ${
                    hasStartedProcess && identificationAccess.isComplete && exchangeDaysRemaining !== null && exchangeDaysRemaining >= 0
                      ? 'text-purple-700 dark:text-purple-300'
                      : 'text-purple-500 dark:text-purple-400'
                  }`}>Phase 3</span>
                </div>
                <p className="text-xs text-secondary-600 dark:text-slate-400 mt-1.5">Acquisition</p>
              </div>
              {/* Purchase Hub */}
              <div 
                onClick={() => purchaseAccess.canAccess && setIsPurchaseHubOpen(true)}
                className={`group relative w-full p-2 rounded-lg border-2 transition-all duration-300 ${
                  nextStep?.hub === 'purchase'
                    ? 'border-purple-600 dark:border-purple-400 hover:border-purple-700 dark:hover:border-purple-500 shadow-lg shadow-purple-600/20 dark:shadow-purple-400/30 cursor-pointer bg-white dark:bg-slate-800 ring-2 ring-purple-600/20 dark:ring-purple-400/30 animate-pulse-slow'
                    : purchaseAccess.canAccess
                    ? 'border-purple-200/50 dark:border-purple-800/30 hover:border-purple-400 dark:hover:border-purple-600 hover:shadow-md cursor-pointer bg-white dark:bg-slate-800'
                    : 'border-slate-200 dark:border-slate-700 opacity-50 bg-slate-50 dark:bg-slate-800/20'
                }`}
              >
                {!purchaseAccess.canAccess && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white/5 dark:bg-slate-900/10 backdrop-blur-[0.5px] rounded-xl z-10 pointer-events-none">
                    <div className="text-center bg-white/85 dark:bg-slate-800/85 px-3 py-2 rounded-lg border-2 border-slate-300 dark:border-slate-600">
                      <Lock className="h-4 w-4 text-slate-600 dark:text-slate-300 mx-auto mb-1" />
                      <p className="text-xs text-slate-700 dark:text-slate-200 font-semibold">{purchaseAccess.message || 'Please complete the Identification Hub first'}</p>
                    </div>
                  </div>
                )}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-purple-500/10 dark:bg-purple-500/20">
                      <Building2 className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-secondary-900 dark:text-slate-100">Purchase Hub</h4>
                      <p className="text-xs text-secondary-600 dark:text-slate-400">Replacement property purchase</p>
                    </div>
                  </div>
                  {purchaseAccess.completionPercent === 100 ? (
                    <CheckCircle2 className="h-4 w-4 text-green-500 dark:text-green-400" />
                  ) : purchaseAccess.canAccess ? (
                    <div className="h-4 w-4 rounded-full border-2 border-purple-300 dark:border-purple-600 flex items-center justify-center">
                      <div className="h-1.5 w-1.5 rounded-full bg-purple-500 dark:bg-purple-400" />
                    </div>
                  ) : null}
                </div>
                <div className="space-y-1.5">
                  <div className="h-2 bg-purple-100 dark:bg-purple-900/30 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-purple-500 dark:bg-purple-400 transition-all duration-500"
                      style={{ width: `${purchaseAccess.completionPercent}%` }}
                    />
                  </div>
                  <p className="text-xs font-medium text-purple-600 dark:text-purple-400">{purchaseAccess.completionPercent}% complete</p>
                </div>
              </div>

              {/* Disbursement Hub */}
              <div 
                onClick={() => disbursementAccess.canAccess && setIsDisbursementHubOpen(true)}
                className={`group relative w-full p-2 rounded-lg border-2 transition-all duration-300 ${
                  nextStep?.hub === 'disbursement'
                    ? 'border-orange-600 dark:border-orange-400 hover:border-orange-700 dark:hover:border-orange-500 shadow-lg shadow-orange-600/20 dark:shadow-orange-400/30 cursor-pointer bg-white dark:bg-slate-800 ring-2 ring-orange-600/20 dark:ring-orange-400/30 animate-pulse-slow'
                    : disbursementAccess.canAccess
                    ? 'border-orange-200/50 dark:border-orange-800/30 hover:border-orange-400 dark:hover:border-orange-600 hover:shadow-md cursor-pointer bg-white dark:bg-slate-800'
                    : 'border-slate-200 dark:border-slate-700 opacity-50 bg-slate-50 dark:bg-slate-800/20'
                }`}
              >
                {!disbursementAccess.canAccess && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white/5 dark:bg-slate-900/10 backdrop-blur-[0.5px] rounded-xl z-10 pointer-events-none">
                    <div className="text-center bg-white/85 dark:bg-slate-800/85 px-3 py-2 rounded-lg border-2 border-slate-300 dark:border-slate-600">
                      <Lock className="h-4 w-4 text-slate-600 dark:text-slate-300 mx-auto mb-1" />
                      <p className="text-xs text-slate-700 dark:text-slate-200 font-semibold">{disbursementAccess.message || 'Please complete the Purchase Hub first'}</p>
                    </div>
                  </div>
                )}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-orange-500/10 dark:bg-orange-500/20">
                      <DollarSign className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-secondary-900 dark:text-slate-100">Disbursement Hub</h4>
                      <p className="text-xs text-secondary-600 dark:text-slate-400">Wire transfer details</p>
                    </div>
                  </div>
                  {disbursementAccess.completionPercent === 100 ? (
                    <CheckCircle2 className="h-4 w-4 text-green-500 dark:text-green-400" />
                  ) : disbursementAccess.canAccess ? (
                    <div className="h-4 w-4 rounded-full border-2 border-orange-300 dark:border-orange-600 flex items-center justify-center">
                      <div className="h-1.5 w-1.5 rounded-full bg-orange-500 dark:bg-orange-400" />
                    </div>
                  ) : null}
                </div>
                <div className="space-y-1.5">
                  <div className="h-2 bg-orange-100 dark:bg-orange-900/30 rounded-full overflow-hidden">
                    <div 
                      className="h-full transition-all duration-500"
                      style={{ 
                        width: `${disbursementAccess.completionPercent}%`,
                        background: 'linear-gradient(to right, #69adba, #8bc6d0, #69adba)'
                      }}
                    />
                  </div>
                  <p className="text-xs font-medium text-orange-600 dark:text-orange-400">{disbursementAccess.completionPercent}% complete</p>
                </div>
                {disbursementAccess.canAccess && nextStep?.hub !== 'disbursement' && (
                  <div className="absolute top-2 right-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        toggleHubLock('disbursement')
                      }}
                      className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                      title={isHubLocked('disbursement') ? 'Unlock hub' : 'Lock hub'}
                    >
                      {isHubLocked('disbursement') ? (
                        <Lock className="h-4 w-4 text-slate-500 dark:text-slate-400" />
                      ) : (
                        <Lock className="h-4 w-4 text-orange-500 dark:text-orange-400" />
                      )}
                    </button>
                  </div>
                )}
              </div>

            </div>
          </div>
        </Card>

      <div className="grid grid-cols-1 gap-6">
        {/* Recent Activity */}
        {user && (
          <Card className="p-6 dark:bg-slate-800 dark:border-slate-700">
            <h2 className="text-lg font-semibold text-secondary-900 dark:text-slate-100 mb-4">Recent Activity</h2>
            <div className="space-y-4">
              {recentActivities.length > 0 ? (
                recentActivities.map((activity) => (
                  <div key={activity.id} className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      {activity.status === 'completed' ? (
                        <CheckCircle className="h-5 w-5 text-green-500" />
                      ) : (
                        <Clock className="h-5 w-5 text-yellow-500" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-secondary-900 dark:text-slate-100">{activity.title}</p>
                      <p className="text-sm text-secondary-600 dark:text-slate-400">{activity.description}</p>
                      <p className="text-xs text-secondary-500 dark:text-slate-500 mt-1">{activity.timestamp}</p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-secondary-600 dark:text-slate-400 text-center py-4">No recent activity yet</p>
              )}
            </div>
            {allActivities.length > 10 && (
              <div className="mt-4">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full"
                  onClick={() => {
                    setIsAllActivityModalOpen(true)
                    setCurrentPage(1)
                  }}
                >
                  View All Activity ({allActivities.length})
                </Button>
              </div>
            )}
          </Card>
        )}
      </div>

      {/* All Activity Modal */}
      {isAllActivityModalOpen && (
        <div 
          className="modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setIsAllActivityModalOpen(false)
            }
          }}
        >
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-4xl max-h-[calc(100vh-8rem)] overflow-hidden flex flex-col my-4">
            {/* Header */}
            <div className="border-b border-slate-200 dark:border-slate-700 px-6 py-4 flex items-center justify-between bg-gradient-to-r from-[#69adba]/10 to-transparent dark:from-[#69adba]/20">
              <div>
                <h2 className="text-xl font-semibold text-secondary-900 dark:text-slate-100">All Activity</h2>
                <p className="text-sm text-secondary-600 dark:text-slate-400 mt-1">
                  {allActivities.length} total {allActivities.length === 1 ? 'activity' : 'activities'}
                </p>
              </div>
              <button 
                onClick={() => setIsAllActivityModalOpen(false)}
                className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-secondary-600 dark:text-slate-400" />
              </button>
            </div>

            {/* Activities List */}
            <div className="flex-1 overflow-y-auto p-6">
              {paginatedActivities.length > 0 ? (
                <div className="space-y-4">
                  {paginatedActivities.map((activity) => (
                    <div 
                      key={activity.id} 
                      className="flex items-start space-x-3 p-3 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
                    >
                      <div className="flex-shrink-0 mt-0.5">
                        {activity.status === 'completed' ? (
                          <CheckCircle className="h-5 w-5 text-green-500 dark:text-green-400" />
                        ) : (
                          <Clock className="h-5 w-5 text-yellow-500 dark:text-yellow-400" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-secondary-900 dark:text-slate-100">{activity.title}</p>
                        <p className="text-sm text-secondary-600 dark:text-slate-400 mt-0.5">{activity.description}</p>
                        <p className="text-xs text-secondary-500 dark:text-slate-500 mt-1.5">{activity.timestamp}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Clock className="h-12 w-12 text-slate-400 dark:text-slate-500 mx-auto mb-3" />
                  <p className="text-sm text-secondary-600 dark:text-slate-400">No activities found</p>
                </div>
              )}
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="border-t border-slate-200 dark:border-slate-700 px-6 py-4 bg-slate-50 dark:bg-slate-800/50">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-secondary-600 dark:text-slate-400">
                    Showing {startIndex + 1} to {Math.min(endIndex, allActivities.length)} of {allActivities.length} activities
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="flex items-center space-x-1"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      <span>Previous</span>
                    </Button>
                    <div className="flex items-center space-x-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum: number
                        if (totalPages <= 5) {
                          pageNum = i + 1
                        } else if (currentPage <= 3) {
                          pageNum = i + 1
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i
                        } else {
                          pageNum = currentPage - 2 + i
                        }
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-all ${
                              currentPage === pageNum
                                ? 'bg-[#69adba] text-white shadow-md'
                                : 'bg-white dark:bg-slate-700 text-secondary-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-600'
                            }`}
                          >
                            {pageNum}
                          </button>
                        )
                      })}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="flex items-center space-x-1"
                    >
                      <span>Next</span>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Follow-on Products Section */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-secondary-900 dark:text-slate-100">Additional Services</h2>
            <p className="text-sm text-secondary-600 dark:text-slate-400 mt-1">
              Enhance your 1031 exchange experience with our partner services
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 border border-slate-200 dark:border-slate-700 rounded-lg hover:border-[#69adba] dark:hover:border-[#69adba] transition-colors cursor-pointer group">
            <div className="flex items-start space-x-3">
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                <FileText className="h-5 w-5 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-secondary-900 dark:text-slate-100 mb-1">
                  Tax Preparation
                </h3>
                <p className="text-xs text-secondary-600 dark:text-slate-400 mb-2">
                  Partner with TurboTax for seamless tax filing. Get expert help with your 1031 exchange tax forms.
                </p>
                <span className="text-xs text-[#69adba] dark:text-[#69adba] font-medium">
                  Coming Soon →
                </span>
              </div>
            </div>
          </div>
          <div className="p-4 border border-slate-200 dark:border-slate-700 rounded-lg hover:border-[#69adba] dark:hover:border-[#69adba] transition-colors cursor-pointer group">
            <div className="flex items-start space-x-3">
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                <Calculator className="h-5 w-5 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-secondary-900 dark:text-slate-100 mb-1">
                  Cost Segregation
                </h3>
                <p className="text-xs text-secondary-600 dark:text-slate-400 mb-2">
                  Maximize your tax benefits with professional cost segregation studies for your replacement property.
                </p>
                <span className="text-xs text-[#69adba] dark:text-[#69adba] font-medium">
                  Coming Soon →
                </span>
              </div>
            </div>
          </div>
        </div>
      </Card>


      {/* Wizard Hub Modal */}
      {isWizardOpen && (
        <ExchangeWizardHub 
          initialTab={wizardInitialTab}
          onClose={() => setIsWizardOpen(false)}
        />
      )}

      {/* Exchange Hub Modal */}
      {isExchangeHubOpen && (
        <ExchangeHub 
          onClose={() => setIsExchangeHubOpen(false)}
        />
      )}

      {/* Acquisition Hub Modal */}
      {isPurchaseHubOpen && (
        <PurchaseHub 
          onClose={() => setIsPurchaseHubOpen(false)}
        />
      )}

      {isDisbursementHubOpen && (
        <DisbursementHub 
          onClose={() => setIsDisbursementHubOpen(false)}
        />
      )}

      {/* KYC Verification Modal */}
      <KYCVerificationModal
        isOpen={showKYCModal}
        onClose={() => setShowKYCModal(false)}
        onComplete={() => {
          setShowKYCModal(false)
          if (user?.email && String(user.email).toLowerCase().trim() === 'demo1@open1031.ai') {
            setWizardInitialTab('initial')
            setIsWizardOpen(true)
            sessionStorage.setItem('sales_hub_shown_demo1_session', 'true')
            demoGuidanceStageRef.current = ''
            updateDemoGuidanceAlert()
          }
        }}
      />
      
    </div>
  )
}
